import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, Link, Spinner, Text, useColorMode } from 'theme-ui';
import { FiTrash2, FiFileText, FiLink, FiMessageCircle } from 'react-icons/fi';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import toast from 'react-hot-toast';
import { getFullUrl } from '../../../utils/global';
import { getStageBadgeStyle } from '../../../utils/constent';
import { uploadMedia } from '../../../store/api';
import { ActionUpdateById } from '../../../store/actions/CommonAction';
import {
  IDeliveryNoteRow,
  IPdfDocItem,
  ISheetIndexItem,
  EditDeliveryDetailsModalProps,
  SheetData,
  ParseResult,
  IUserRecord,
  DeliveryLinkSectionProps,
  DeliveryFormData,
} from './types';
import DeliveryTable from './DeliveryTable';
import DeliverySectionCard, { DeliveryEmptyState } from './DeliverySectionCard';
import {
  parseInput,
  formatSheetIndexPayload,
  parseDeliveryRecord,
  buildDeliverActionPayload,
} from './deliveryModalUtils';
import { createActionHighlight, editActionHighlight } from '../../../store/actions/action.Highlighters';
import { ExpandableText } from '../../../utils/GenerateQueryString';

export type { IDeliveryNoteRow, IPdfDocItem, ISheetIndexItem, EditDeliveryDetailsModalProps, SheetData, ParseResult };

const tableSlotSx = (grow: number, minHeight: number) => ({
  flex: `${grow} 1 0`,
  minHeight: [`${Math.round(minHeight * 1.6)}px`, `${Math.round(minHeight * 1.6)}px`, `${Math.round(minHeight * 1.6)}px`, `${minHeight}px`],
  display: 'flex',
  '& > *': { flex: 1, minWidth: 0 },
});

const emptyFormData: DeliveryFormData = {
  comments: [],
  tradeNotes: [],
  gdLinks: [],
  frontAppLinks: [],
  sheetIndexes: [],
  pdfDocs: [],
};

const DeliveryLinkSection: React.FC<DeliveryLinkSectionProps> = ({
  title,
  subtitle,
  placeholder,
  links,
  onAddLink,
  onDeleteLink,
  isCompletedTab,
  theme,
}) => {
  const [showAdd, setShowAdd] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(() => {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    try {
      const url = new URL(trimmed);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        setError('Please enter a valid URL (must start with http:// or https://)');
        return;
      }
    } catch {
      setError('Please enter a valid URL (must start with http:// or https://)');
      return;
    }
    setError(null);
    onAddLink(trimmed);
    setInputVal('');
    setShowAdd(false);
  }, [inputVal, onAddLink]);

  return (
    <DeliverySectionCard
      title={title}
      subtitle={subtitle}
      actionText={isCompletedTab ? null : '+ Upload Link'}
      onActionClick={() => setShowAdd((prev) => !prev)}
      height="auto"
      sectionBg={theme.sectionBg}
      borderColor={theme.borderColor}
      textColor={theme.textColor}
      subTextColor={theme.subTextColor}
      primaryColor={theme.primaryColor}
      actionInput={
        !isCompletedTab && showAdd ? (
          <>
            <Flex sx={{ gap: '6px', mb: error ? '4px' : '10px' }}>
              <input
                type="text"
                placeholder={placeholder}
                value={inputVal}
                onChange={(e) => {
                  setInputVal(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                style={{
                  flex: 1,
                  fontSize: '12px',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: `1px solid ${error ? '#EF4444' : theme.borderColor}`,
                  backgroundColor: theme.cardBg,
                  color: theme.textColor,
                }}
              />
              <Button
                onClick={handleSubmit}
                variant="SaveButton"
                sx={{ fontSize: '11px', padding: '4px 12px', height: 'auto', cursor: 'pointer' }}
              >
                Add
              </Button>
            </Flex>
            {error && (
              <Box sx={{ color: '#EF4444', fontSize: '11px', mb: '6px' }}>
                {error}
              </Box>
            )}
          </>
        ) : null
      }
    >
      {links.map((link, idx) => (
        <Flex
          key={idx}
          variant="cards.deliveryCardItem"
          sx={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.borderColor}` }}
        >
          <Link
            href={link}
            target="_blank"
            rel="noreferrer"
            title={link}
            variant="cards.deliveryLink"
          >
            {link}
          </Link>
          {!isCompletedTab && (
            <Flex sx={{ gap: '8px', alignItems: 'center', flexShrink: 0 }}>
              <Box
                onClick={() => onDeleteLink(idx)}
                variant="cards.deliveryDeleteIcon"
                title="Delete link"
              >
                <FiTrash2 size={14} />
              </Box>
            </Flex>
          )}
        </Flex>
      ))}
      {links.length === 0 && (
        <DeliveryEmptyState icon={<FiLink size={22} />} text="No links added yet" color={theme.subTextColor} />
      )}
    </DeliverySectionCard>
  );
};

export const EditDeliveryDetailsModal: React.FC<EditDeliveryDetailsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  deliveryRecord,
  onSave,
  isManualDelivery,
  isCompletedTab,
  isEditAllowed = true,
}) => {
  const isManual = Boolean(isManualDelivery || deliveryRecord?.manualDeliver);
  const dispatch = useDispatch();
  const { uploadedMedia, loader: mediaLoader } = useSelector(
    (state: { media?: { uploadedMedia?: unknown; loader?: boolean } }) => state?.media || {}
  );
  const dataForTable = useSelector(
    (state: { masterData?: { dataForTable?: Record<string, unknown> } }) => state.masterData?.dataForTable
  );

  const rawUsers = useMemo<IUserRecord[]>(() => {
    const dataObj = (dataForTable?.data as Record<string, unknown> | undefined) || dataForTable;
    return (Array.isArray(dataObj?.allProjectManagers) ? dataObj?.allProjectManagers : []) as IUserRecord[];
  }, [dataForTable]);

  const [colorMode] = useColorMode();
  const isDark = colorMode === 'dark';

  const themeTokens = useMemo(
    () => ({
      cardBg: isDark ? '#1E293B' : '#FFFFFF',
      sectionBg: isDark ? '#1c2638' : '#F9FAFB',
      borderColor: isDark ? '#334155' : '#E5E7EB',
      textColor: isDark ? '#F8FAFC' : '#1E293B',
      subTextColor: isDark ? '#94A3B8' : '#64748B',
      primaryColor: '#7743DB',
    }),
    [isDark]
  );
  const { cardBg, sectionBg, borderColor, textColor, subTextColor, primaryColor } = themeTokens;

  const [formData, setFormData] = useState<DeliveryFormData>(emptyFormData);
  const [savedData, setSavedData] = useState<DeliveryFormData>(emptyFormData);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [newComment, setNewComment] = useState<string>('');
  const [showAddComment, setShowAddComment] = useState<boolean>(false);
  const [activeTrade, setActiveTrade] = useState<string>('');
  const [sheetTextarea, setSheetTextarea] = useState<string>('');
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [showAddSheet, setShowAddSheet] = useState<boolean>(false);
  const [editingSheetId, setEditingSheetId] = useState<string | null>(null);
  const [isDelivering, setIsDelivering] = useState<boolean>(false);
  const [isSavingAll, setIsSavingAll] = useState<boolean>(false);
  const [showConfirmDeliver, setShowConfirmDeliver] = useState<boolean>(false);
  const [isUploadingDocs, setIsUploadingDocs] = useState<boolean>(false);

  const updateField = useCallback(
    <K extends keyof DeliveryFormData>(
      key: K,
      updater: DeliveryFormData[K] | ((prev: DeliveryFormData[K]) => DeliveryFormData[K])
    ) => {
      setFormData((prev) => ({
        ...prev,
        [key]: typeof updater === 'function' ? (updater as (p: DeliveryFormData[K]) => DeliveryFormData[K])(prev[key]) : updater,
      }));
    },
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    setIsDelivering(false);

    if (deliveryRecord) {
      const parsed = parseDeliveryRecord(deliveryRecord, rawUsers);
      const data: DeliveryFormData = {
        comments: parsed.comments,
        tradeNotes: parsed.tradeNotes,
        gdLinks: parsed.gdLinks,
        frontAppLinks: parsed.frontAppLinks,
        sheetIndexes: parsed.sheetIndex,
        pdfDocs: parsed.pdfDocs,
      };
      setFormData(data);
      setSavedData(data);
      setSheetTextarea('');
      setShowAddSheet(false);
      setSheetError(null);
    }
  }, [deliveryRecord, isOpen, rawUsers]);

  useEffect(() => {
    if (uploadedMedia) {
      let uploadedName = '';
      if (typeof uploadedMedia === 'string') {
        uploadedName = uploadedMedia;
      } else if (Array.isArray(uploadedMedia) && uploadedMedia.length > 0) {
        const item = uploadedMedia[0];
        uploadedName = typeof item === 'string' ? item : item?.file || item?.link || item?.name || item?.filename || '';
      } else if (typeof uploadedMedia === 'object' && uploadedMedia !== null) {
        const obj = uploadedMedia as { file?: string; link?: string; name?: string; url?: string; filename?: string };
        uploadedName = obj.file || obj.link || obj.name || obj.url || obj.filename || '';
      }

      if (uploadedName) {
        setFormData((prev) => {
          if (prev.pdfDocs.some((p) => p.name === uploadedName)) return prev;
          toast.success('Document uploaded successfully');
          return {
            ...prev,
            pdfDocs: [
              ...prev.pdfDocs,
              {
                id: String(Date.now()),
                name: uploadedName,
                size: '',
              },
            ],
          };
        });
      }
    }
  }, [uploadedMedia]);

  const availableTrades = useMemo<string[]>(() => {
    const deliveryNoteTaskIds = new Set<string>();

    const notes = deliveryRecord?.deliveryNotes;
    const rawNotes = Array.isArray(notes) ? notes : [];

    rawNotes.forEach((item: unknown) => {
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>;
        const rawTId = obj.taskId;
        if (typeof rawTId === 'string' && rawTId.trim()) {
          deliveryNoteTaskIds.add(rawTId.trim());
        } else if (rawTId && typeof rawTId === 'object') {
          const idVal = String(
            (rawTId as Record<string, unknown>)._id ||
            (rawTId as Record<string, unknown>).id ||
            ''
          ).trim();
          if (idVal) deliveryNoteTaskIds.add(idVal);
        }
      }
    });

    formData.tradeNotes.forEach((n) => {
      if (n.taskId) {
        deliveryNoteTaskIds.add(String(n.taskId).trim());
      }
    });

    const existingTrades = new Set(
      formData.tradeNotes
        .map((n) => n.trade?.trim())
        .filter((t): t is string => Boolean(t) && t.toUpperCase() !== 'PM')
    );

    const list = deliveryRecord?.tasksNameList || [];
    const matchedTrades: string[] = [];

    if (Array.isArray(list) && list.length > 0) {
      list.forEach((t: unknown) => {
        if (typeof t === 'string') {
          const name = t.trim();
          if (name && name.toUpperCase() !== 'PM' && (existingTrades.has(name) || deliveryNoteTaskIds.has(name))) {
            if (!matchedTrades.includes(name)) matchedTrades.push(name);
          }
          return;
        }

        if (t && typeof t === 'object') {
          const obj = t as Record<string, unknown>;
          const taskName = String(
            obj.name ||
            obj.taskName ||
            (obj.taskDataId && typeof obj.taskDataId === 'object'
              ? (obj.taskDataId as Record<string, unknown>).name
              : '') ||
            ''
          ).trim();

          if (!taskName || taskName.toUpperCase() === 'PM') return;

          const ids = [
            obj._id,
            obj.id,
            obj.taskId,
            typeof obj.taskDataId === 'object' && obj.taskDataId !== null
              ? (obj.taskDataId as Record<string, unknown>)._id || (obj.taskDataId as Record<string, unknown>).id
              : obj.taskDataId,
          ]
            .filter(Boolean)
            .map((id) => String(id).trim());

          const hasTaskIdInDeliveryNotes = ids.some((id) => deliveryNoteTaskIds.has(id));
          const hasNotesForTrade = existingTrades.has(taskName);

          if (hasTaskIdInDeliveryNotes || hasNotesForTrade) {
            if (!matchedTrades.includes(taskName)) {
              matchedTrades.push(taskName);
            }
          }
        }
      });
    }

    if (matchedTrades.length > 0) {
      return matchedTrades;
    }

    const dynamicTrades = Array.from(existingTrades);
    return dynamicTrades;
  }, [deliveryRecord?.tasksNameList, deliveryRecord?.deliveryNotes, formData.tradeNotes]);

  useEffect(() => {
    if (availableTrades.length > 0) {
      if (!activeTrade || !availableTrades.includes(activeTrade)) {
        setActiveTrade(availableTrades[0]);
      }
    } else {
      setActiveTrade('');
    }
  }, [availableTrades, activeTrade]);

  const isCommentsDirty = useMemo(
    () => newComment.trim().length > 0 || JSON.stringify(formData.comments) !== JSON.stringify(savedData.comments),
    [newComment, formData.comments, savedData.comments]
  );
  const isGdLinksDirty = useMemo(
    () => JSON.stringify(formData.gdLinks) !== JSON.stringify(savedData.gdLinks),
    [formData.gdLinks, savedData.gdLinks]
  );
  const isFrontAppDirty = useMemo(
    () => JSON.stringify(formData.frontAppLinks) !== JSON.stringify(savedData.frontAppLinks),
    [formData.frontAppLinks, savedData.frontAppLinks]
  );
  const isSheetIndexDirty = useMemo(
    () => JSON.stringify(formData.sheetIndexes) !== JSON.stringify(savedData.sheetIndexes),
    [formData.sheetIndexes, savedData.sheetIndexes]
  );
  const isPdfDocsDirty = useMemo(
    () => JSON.stringify(formData.pdfDocs) !== JSON.stringify(savedData.pdfDocs),
    [formData.pdfDocs, savedData.pdfDocs]
  );
  const isTradeNotesDirty = useMemo(
    () =>
      JSON.stringify(formData.tradeNotes.map((n) => ({ id: n.id, include: n.include }))) !==
      JSON.stringify(savedData.tradeNotes.map((n) => ({ id: n.id, include: n.include }))),
    [formData.tradeNotes, savedData.tradeNotes]
  );

  const isAnySectionDirty = useMemo(
    () =>
      isCommentsDirty ||
      isGdLinksDirty ||
      isFrontAppDirty ||
      isSheetIndexDirty ||
      isPdfDocsDirty ||
      isTradeNotesDirty,
    [isCommentsDirty, isGdLinksDirty, isFrontAppDirty, isSheetIndexDirty, isPdfDocsDirty, isTradeNotesDirty]
  );

  const isSaveDisabled = !isAnySectionDirty || isSavingAll || isDelivering || Boolean(mediaLoader) || isUploadingDocs;

  const isPublishingReady = Boolean(deliveryRecord?.publishingReady);
  const statusLower = String(deliveryRecord?.status || '').toLowerCase().trim();
  const isAlreadyDelivered = statusLower === 'delivered' || statusLower === 'ipr delivered';
  const isWaitingForPaymentRed =
    !Boolean(deliveryRecord?.waitingForPayment) &&
    statusLower.includes('ready to deliver');
  const hasDeliveryDate = Boolean(
    deliveryRecord?.deliveryDate && String(deliveryRecord.deliveryDate).trim()
  );

  const isDeliverDisabled =
    !isEditAllowed ||
    !isPublishingReady ||
    !hasDeliveryDate ||
    isDelivering ||
    isAlreadyDelivered ||
    isWaitingForPaymentRed ||
    isAnySectionDirty;

  const handleToggleInclude = useCallback((id: string) => {
    const targetNote = formData.tradeNotes.find((n) => n.id === id);
    if (!targetNote) return;

    const newIncludeStatus = !targetNote.include;
    const updatedTradeNotes = formData.tradeNotes.map((n) =>
      n.id === id ? { ...n, include: newIncludeStatus } : n
    );
    updateField('tradeNotes', updatedTradeNotes);

    if (targetNote.actionId) {
      const projId =
        targetNote.projectId ||
        (typeof deliveryRecord?.projectId === 'object' && deliveryRecord?.projectId !== null
          ? String((deliveryRecord.projectId as Record<string, unknown>)._id || '')
          : String(deliveryRecord?.projectId || ''));

      const stgId = targetNote.stageId || String(deliveryRecord?.id || (deliveryRecord as unknown as { _id?: string })?._id || '');

      setSavingSection('Delivery Notes');
      dispatch(
        ActionUpdateById(
          {
            Query: `/actions/edit-in-description/${targetNote.actionId}`,
            obj: {
              actionType: 'Delivery Notes',
              data: { toDisplay: newIncludeStatus },
              name: targetNote.note,
              projectId: projId,
              stageId: stgId,
              taskId: targetNote.taskId || null,
            },
          },
          () => setSavingSection(null)
        )
      );
    } else if (deliveryRecord?.id) {
      setSavingSection('Delivery Notes');
      dispatch(
        ActionUpdateById(
          {
            Query: `/deliveries/edit-delivery/${deliveryRecord.id}`,
            obj: {
              deliveryNotes: updatedTradeNotes.map((n) => ({
                ...n,
                toDisplay: Boolean(n.include),
                isChecked: Boolean(n.include),
              })),
            },
          },
          () => setSavingSection(null)
        )
      );
    }
  }, [formData.tradeNotes, deliveryRecord, dispatch, updateField]);

  const handleEditNote = useCallback((id: string, newText: string) => {
    const targetNote = formData.tradeNotes.find((n) => n.id === id);
    if (!targetNote) return;

    const previousText = targetNote.note;
    const applyText = (text: string) =>
      updateField('tradeNotes', (prev) => prev.map((n) => (n.id === id ? { ...n, note: text } : n)));
    applyText(newText);

    const getRefId = (val: unknown): string | undefined => {
      if (!val) return undefined;
      if (typeof val === 'string') return val;
      if (typeof val === 'object' && '_id' in (val as object)) return (val as { _id?: string })._id || undefined;
      return undefined;
    };

    if (targetNote.actionId) {
      const raw = (targetNote.rawItem || {}) as Record<string, unknown>;
      const projId =
        targetNote.projectId ||
        (typeof deliveryRecord?.projectId === 'object' && deliveryRecord?.projectId !== null
          ? String((deliveryRecord.projectId as Record<string, unknown>)._id || '')
          : String(deliveryRecord?.projectId || ''));
      const stgId = targetNote.stageId || String(deliveryRecord?.id || (deliveryRecord as unknown as { _id?: string })?._id || '');

      setSavingSection('Delivery Notes');
      dispatch(
        editActionHighlight(
          {
            id: targetNote.id,
            _id: targetNote.id,
            name: previousText,
            actionType: 'Delivery Notes',
            projectId: projId,
            stageId: stgId,
            taskId: targetNote.taskId || null,
            data: {
              name: newText,
              revisionId: getRefId(raw.revisionId) || getRefId(raw.revision),
              planCheckId: getRefId(raw.planCheckId) || getRefId(raw.planCheck),
              stageId: getRefId(raw.stageId) || getRefId(raw.stage),
            },
          },
          targetNote.actionId,
          (res: { status?: number | boolean; data?: { status?: boolean; message?: string } }) => {
            setSavingSection(null);
            if (res?.data?.status || res?.status === 200) {
              toast.success('Delivery note updated');
            } else {
              applyText(previousText);
              toast.error(res?.data?.message || 'Failed to update delivery note');
            }
          }
        )
      );
    } else if (deliveryRecord?.id) {
      setSavingSection('Delivery Notes');
      dispatch(
        ActionUpdateById(
          {
            Query: `/deliveries/edit-delivery/${deliveryRecord.id}`,
            obj: {
              deliveryNotes: formData.tradeNotes.map((n) => ({
                ...n,
                note: n.id === id ? newText : n.note,
                toDisplay: Boolean(n.include),
                isChecked: Boolean(n.include),
              })),
            },
          },
          () => setSavingSection(null)
        )
      );
    }
  }, [formData.tradeNotes, deliveryRecord, dispatch, updateField]);

  const activeRevisions = useMemo(
    () =>
      Array.from(
        new Set(formData.tradeNotes.map((n) => (n.revision || '').trim()).filter((r) => r && r !== '—' && r !== '-'))
      ),
    [formData.tradeNotes]
  );

  const handleDeleteGdLink = useCallback((index: number) => {
    updateField('gdLinks', (prev) => prev.filter((_, i) => i !== index));
  }, [updateField]);

  const handleDeleteFrontAppLink = useCallback((index: number) => {
    updateField('frontAppLinks', (prev) => prev.filter((_, i) => i !== index));
  }, [updateField]);

  const handleDeletePdf = useCallback((id: string) => {
    updateField('pdfDocs', (prev) => prev.filter((p) => p.id !== id));
  }, [updateField]);

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      setIsUploadingDocs(true);
      const newDocs: IPdfDocItem[] = [];
      let failureCount = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('type', 'actionAttachments');
        formDataUpload.append('uploadType', 'File');

        try {
          const response = await uploadMedia(formDataUpload);
          if (response?.data?.status && (response?.data?.data || response?.data)) {
            const resData = response?.data?.data || response?.data;
            let uploadedName = '';
            if (typeof resData === 'string') {
              uploadedName = resData;
            } else if (Array.isArray(resData) && resData.length > 0) {
              const item = resData[0];
              uploadedName =
                typeof item === 'string'
                  ? item
                  : (item as { file?: string; link?: string; name?: string })?.file ||
                  (item as { file?: string; link?: string; name?: string })?.link ||
                  (item as { file?: string; link?: string; name?: string })?.name ||
                  '';
            } else if (typeof resData === 'object' && resData !== null) {
              const obj = resData as { file?: string; link?: string; name?: string; url?: string; filename?: string };
              uploadedName = obj.file || obj.link || obj.name || obj.url || obj.filename || '';
            }

            if (uploadedName) {
              newDocs.push({
                id: `${Date.now()}_${i}`,
                name: uploadedName,
                size: '',
              });
            }
          } else {
            failureCount++;
          }
        } catch {
          failureCount++;
        }
      }

      if (newDocs.length > 0) {
        setFormData((prev) => {
          const existingNames = new Set(prev.pdfDocs.map((p) => p.name));
          const uniqueNew = newDocs.filter((doc) => !existingNames.has(doc.name));
          return {
            ...prev,
            pdfDocs: [...prev.pdfDocs, ...uniqueNew],
          };
        });
        toast.success(
          newDocs.length === 1
            ? 'Document uploaded successfully'
            : `${newDocs.length} documents uploaded successfully`
        );
      }

      if (failureCount > 0) {
        toast.error(`Failed to upload ${failureCount} file(s)`);
      }

      setIsUploadingDocs(false);
      e.target.value = '';
    },
    []
  );

  const handleStartEditItem = useCallback((item: ISheetIndexItem) => {
    const sNo = item.sheetNo || '';
    const sTitle = item.sheetTitle || item.name || '';
    const sDisc = item.discipline || '';
    const sStat = item.status || 'UPDATED';
    const text = (sNo || sDisc)
      ? `#${sNo} - ${sTitle} - ${sDisc} - ${sStat}`
      : sTitle;

    setEditingSheetId(item._id || item.id);
    setSheetTextarea(text);
    setShowAddSheet(true);
    setSheetError(null);
  }, []);

  const handleDeleteSheetItem = useCallback((idToDelete: string) => {
    updateField('sheetIndexes', (prev) =>
      prev.filter((item) => (item._id || item.id) !== idToDelete && item.id !== idToDelete)
    );
  }, [updateField]);

  const handleAddSheetSubmit = useCallback(() => {
    setSheetError(null);
    if (!sheetTextarea.trim() || !sheetTextarea.replace(/[\s#]/g, '')) {
      const emptyMsg = 'Please enter sheet data starting with #';
      setSheetError(emptyMsg);
      return;
    }

    const res = parseInput(sheetTextarea, true);
    if (res.length > 0 && typeof res[0] === 'string') {
      const errList = res as string[];
      const errMsg = errList.join('\n');
      setSheetError(errMsg);
      return;
    }

    const validData = res as SheetData[];

    if (editingSheetId) {
      if (validData.length > 1) {
        setSheetError('Only 1 sheet is allowed while updating');
        return;
      }
      const updated = validData[0];
      if (updated) {
        updateField('sheetIndexes', (prev) =>
          prev.map((item) =>
            item._id === editingSheetId || item.id === editingSheetId
              ? {
                ...item,
                name: `${updated.sheetNo} - ${updated.sheetTitle} - ${updated.discipline} - ${updated.status}`,
                sheetNo: updated.sheetNo,
                sheetTitle: updated.sheetTitle,
                discipline: updated.discipline,
                status: updated.status,
                isUpdated: updated.status.toLowerCase() === 'updated',
              }
              : item
          )
        );
      }
    } else {
      const newItems: ISheetIndexItem[] = validData.map((item, idx) => ({
        id: String(Date.now() + idx),
        name: `${item.sheetNo} - ${item.sheetTitle} - ${item.discipline} - ${item.status}`,
        sheetNo: item.sheetNo,
        sheetTitle: item.sheetTitle,
        discipline: item.discipline,
        status: item.status,
        isUpdated: item.status.toLowerCase() === 'updated',
      }));
      updateField('sheetIndexes', (prev) => [...prev, ...newItems]);
    }

    setSheetTextarea('');
    setEditingSheetId(null);
    setSheetError(null);
    setShowAddSheet(false);
  }, [sheetTextarea, editingSheetId, updateField]);

  const handleAddCommentSubmit = useCallback(() => {
    if (newComment.trim()) {
      updateField('comments', (prev) => [...prev, newComment.trim()]);
      setNewComment('');
      setShowAddComment(false);
    }
  }, [newComment, updateField]);

  const handleDeleteComment = useCallback((index: number) => {
    updateField('comments', (prev) => prev.filter((_, i) => i !== index));
  }, [updateField]);

  const handleSaveAll = () => {
    if (isSavingAll || isDelivering || isSaveDisabled) return;

    let updatedComments = [...formData.comments];
    if (newComment.trim()) {
      updatedComments.push(newComment.trim());
      setNewComment('');
      setShowAddComment(false);
    }

    let updatedSheetIndexes = [...formData.sheetIndexes];
    if (sheetTextarea.trim() && sheetTextarea.replace(/[\s#]/g, '')) {
      const res = parseInput(sheetTextarea, true);
      if (Array.isArray(res) && res.length > 0 && typeof res[0] !== 'string') {
        const validData = res as SheetData[];
        if (editingSheetId) {
          if (validData.length > 1) {
            setSheetError('Only 1 sheet is allowed while updating');
            return;
          }
          const updated = validData[0];
          if (updated) {
            updatedSheetIndexes = updatedSheetIndexes.map((item) =>
              item._id === editingSheetId || item.id === editingSheetId
                ? {
                  ...item,
                  name: `${updated.sheetNo} - ${updated.sheetTitle} - ${updated.discipline} - ${updated.status}`,
                  sheetNo: updated.sheetNo,
                  sheetTitle: updated.sheetTitle,
                  discipline: updated.discipline,
                  status: updated.status,
                  isUpdated: updated.status.toLowerCase() === 'updated',
                }
                : item
            );
          }
        } else {
          const newItems: ISheetIndexItem[] = validData.map((item, idx) => ({
            id: String(Date.now() + idx),
            name: `${item.sheetNo} - ${item.sheetTitle} - ${item.discipline} - ${item.status}`,
            sheetNo: item.sheetNo,
            sheetTitle: item.sheetTitle,
            discipline: item.discipline,
            status: item.status,
            isUpdated: item.status.toLowerCase() === 'updated',
          }));
          updatedSheetIndexes = [...updatedSheetIndexes, ...newItems];
        }
        setSheetTextarea('');
        setEditingSheetId(null);
        setSheetError(null);
        setShowAddSheet(false);
      }
    }

    const currentData: DeliveryFormData = {
      ...formData,
      comments: updatedComments,
      sheetIndexes: updatedSheetIndexes,
    };
    setFormData(currentData);
    setSavedData(currentData);

    const savePayload: Record<string, unknown> = {};

    if (isCommentsDirty) {
      savePayload.comments = updatedComments.length > 0 ? [{ text: updatedComments.join(', ') }] : [];
    }
    if (isTradeNotesDirty) {
      const formattedTradeNotes = formData.tradeNotes.map((n) => ({
        ...n,
        toDisplay: Boolean(n.include),
        isChecked: Boolean(n.include),
      }));
      const deliveryNoteObjectIds = formattedTradeNotes
        .map((n) => n.actionId || n.id)
        .filter((id): id is string => Boolean(id && id.length === 24 && /^[0-9a-fA-F]{24}$/.test(id)));
      savePayload.deliveryNotes = deliveryNoteObjectIds;
    }
    if (isGdLinksDirty) {
      savePayload.gdLinks = formData.gdLinks.map((l) => ({ link: l }));
    }
    if (isPdfDocsDirty) {
      const cleanPdfDocs = formData.pdfDocs.map((d) => ({ link: d.name }));
      savePayload.pdfDocs = cleanPdfDocs;
      savePayload.uploadedDocuments = cleanPdfDocs;
    }
    if (isSheetIndexDirty) {
      savePayload.sheetIndex = formatSheetIndexPayload(updatedSheetIndexes);
    }
    if (isFrontAppDirty) {
      savePayload.frontAppLinks = formData.frontAppLinks.map((l) => ({ link: l }));
    }

    if (onSave) {
      setIsSavingAll(true);
      onSave(savePayload, 'All', () => {
        setIsSavingAll(false);
      });
    }
  };

  const handleDeliver = () => {
    if (isDelivering || isDeliverDisabled) return;
    setShowConfirmDeliver(true);
  };

  const proceedDeliver = () => {
    const rawDeliveryDate = (deliveryRecord as { deliveryDate?: string } | null | undefined)?.deliveryDate;
    if (!rawDeliveryDate || !String(rawDeliveryDate).trim()) {
      toast.error('Please select delivery date');
      setIsDelivering(false);
      setShowConfirmDeliver(false);
      return;
    }

    setIsDelivering(true);
    const actionPayload = buildDeliverActionPayload({
      deliveryRecord,
      tradeNotes: formData.tradeNotes,
      commentsList: formData.comments,
      gdLinks: formData.gdLinks,
      frontAppLinks: formData.frontAppLinks,
      sheetIndexes: formData.sheetIndexes,
      pdfDocs: formData.pdfDocs,
    });

    dispatch(
      createActionHighlight(
        actionPayload,
        (res: { status?: number; data?: { message?: string }; message?: string }) => {
          setIsDelivering(false);
          setShowConfirmDeliver(false);
          if (res?.status === 200 || res?.status === 201) {
            toast.success('Successfully delivered!');
            if (onSuccess) onSuccess();
            onClose();
          } else {
            toast.error(res?.data?.message || res?.message || 'Failed to deliver');
          }
        }
      )
    );
  };

  const closeBtn = useMemo(
    () => (
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        style={{
          color: textColor,
          background: 'transparent',
          border: 'none',
          fontSize: 22,
          lineHeight: 1,
          padding: 0,
          cursor: 'pointer',
        }}
      >
        <span aria-hidden="true">&times;</span>
      </button>
    ),
    [onClose, textColor]
  );
  const displayStageName =
    deliveryRecord?.temporaryName ||
    (typeof deliveryRecord?.stageId === 'object' && deliveryRecord?.stageId !== null
      ? (deliveryRecord.stageId as { temporaryName?: string }).temporaryName
      : undefined) ||
    deliveryRecord?.stageName;

  const dynamicStageColor =
    (deliveryRecord as { stageColor?: string })?.stageColor ||
    (typeof deliveryRecord?.stageId === 'object' && deliveryRecord?.stageId !== null
      ? (deliveryRecord.stageId as { color?: string }).color
      : undefined);
  const stageHeaderColor =
    dynamicStageColor && dynamicStageColor !== '#000000' && dynamicStageColor !== '#000'
      ? dynamicStageColor
      : getStageBadgeStyle(deliveryRecord?.stageName || displayStageName || '').color;

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={onClose}
        centered
        size="xl"
        backdropClassName="edit-delivery-modal-backdrop"
        style={{ maxWidth: '1550px', width: '95%', maxHeight: '96vh', margin: '10px auto' }}
      >
        <ModalHeader
          close={closeBtn}
          toggle={onClose}
          style={{
            backgroundColor: cardBg,
            color: textColor,
            borderBottom: `1px solid ${borderColor}`,
            padding: '10px 16px',
          }}
        >
          <span style={{ fontSize: '16px', fontWeight: 700, color: textColor }}>
            Delivery Details:{' '}
            {(deliveryRecord?.projectName || (deliveryRecord as unknown as { name?: string })?.name) ? (
              <>
                <span>{deliveryRecord?.projectName || (deliveryRecord as unknown as { name?: string })?.name}</span>
                {displayStageName && <span style={{ color: subTextColor, margin: '0 6px' }}>&gt;</span>}
              </>
            ) : null}
            {displayStageName && (
              <span style={{ color: stageHeaderColor }}>
                {displayStageName}
              </span>
            )}
          </span>
        </ModalHeader>

        <ModalBody
          style={{
            backgroundColor: cardBg,
            color: textColor,
            padding: 0,
            overflowY: 'auto',
            maxHeight: 'calc(96vh - 105px)',
          }}
        >
          <Box
            sx={{
              padding: '16px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              boxSizing: 'border-box',
              height: ['auto', 'auto', 'auto', 'calc(96vh - 125px)'],
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: ['1fr', '1fr', '1fr', '1fr 2fr'],
                gridTemplateRows: ['none', 'none', 'none', 'minmax(0, 1fr)'],
                gap: '12px',
                alignItems: 'stretch',
                flex: 1,
                minHeight: 0,
              }}
            >
              <Flex sx={{ flexDirection: 'column', gap: '12px', minWidth: 0, minHeight: 0 }}>
                {/* Delivery Comments */}
                <DeliverySectionCard
                  title="Delivery Comments"
                  actionText={isCompletedTab ? null : "+ Add Comment"}
                  onActionClick={() => setShowAddComment(!showAddComment)}
                  height="auto"
                  sectionBg={sectionBg}
                  borderColor={borderColor}
                  textColor={textColor}
                  subTextColor={subTextColor}
                  primaryColor={primaryColor}
                  actionInput={
                    !isCompletedTab && showAddComment ? (
                      <Flex sx={{ gap: '6px', mb: '10px' }}>
                        <input
                          type="text"
                          placeholder="Enter comment..."
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddCommentSubmit();
                            }
                          }}
                          style={{
                            flex: 1,
                            fontSize: '12px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            border: `1px solid ${borderColor}`,
                            backgroundColor: cardBg,
                            color: textColor,
                          }}
                        />
                        <Button
                          onClick={handleAddCommentSubmit}
                          variant="SaveButton"
                          sx={{ fontSize: '11px', padding: '4px 12px', height: 'auto', cursor: 'pointer' }}
                        >
                          Add
                        </Button>
                      </Flex>
                    ) : null
                  }
                >
                  {formData.comments.map((cText, idx) => (
                    <Flex
                      key={idx}
                      variant="cards.deliveryCardItem"
                      sx={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}
                    >
                      <ExpandableText text={cText} maxLength={80} sx={{ flex: 1, minWidth: 0, pr: '8px', color: textColor }} />
                      {!isCompletedTab && (
                        <Box
                          onClick={() => handleDeleteComment(idx)}
                          variant="cards.deliveryDeleteIcon"
                          title="Delete comment"
                        >
                          <FiTrash2 size={14} />
                        </Box>
                      )}
                    </Flex>
                  ))}
                  {formData.comments.length === 0 && (
                    <DeliveryEmptyState icon={<FiMessageCircle size={22} />} text="No comments added yet" color={subTextColor} />
                  )}
                </DeliverySectionCard>

                {/* GD Links */}
                <DeliveryLinkSection
                  title="GD Links"
                  placeholder="https://drive.google.com/..."
                  links={formData.gdLinks}
                  onAddLink={(link) => updateField('gdLinks', (prev) => [...prev, link])}
                  onDeleteLink={handleDeleteGdLink}
                  isCompletedTab={isCompletedTab}
                  theme={themeTokens}
                />

                {/* Uploaded Document (PDF) */}
                <DeliverySectionCard
                  title="Uploaded Document"
                  actionText={
                    isCompletedTab ? null : (
                      <label style={{ fontSize: '12px', color: primaryColor, fontWeight: 600, cursor: 'pointer' }}>
                        + Upload Document
                        <input
                          type="file"
                          multiple
                          accept=".pdf,image/*,.png,.jpg,.jpeg"
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                        />
                      </label>
                    )
                  }
                  height="auto"
                  isLoading={Boolean(mediaLoader) || isUploadingDocs}
                  sectionBg={sectionBg}
                  borderColor={borderColor}
                  textColor={textColor}
                  subTextColor={subTextColor}
                  primaryColor={primaryColor}
                >
                  {formData.pdfDocs.map((doc) => (
                    <Flex
                      key={doc.id}
                      variant="cards.deliveryCardItem"
                      sx={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}
                    >
                      <Flex sx={{ alignItems: 'center', gap: '8px', overflow: 'hidden', flex: 1, mr: '8px' }}>
                        <Box variant="cards.deliveryDocIcon">
                          <FiFileText size={16} />
                        </Box>
                        <Box sx={{ overflow: 'hidden', flex: 1 }}>
                          <Link
                            href={getFullUrl(doc.name)}
                            target="_blank"
                            rel="noreferrer"
                            download
                            variant="cards.deliveryLink"
                            title={doc.name.split('/').pop() || doc.name}
                          >
                            {doc.name.split('/').pop() || doc.name}
                          </Link>
                        </Box>
                      </Flex>
                      {!isCompletedTab && (
                        <Box
                          onClick={() => handleDeletePdf(doc.id)}
                          variant="cards.deliveryDeleteIcon"
                        >
                          <FiTrash2 size={14} />
                        </Box>
                      )}
                    </Flex>
                  ))}
                  {formData.pdfDocs.length === 0 && (
                    <DeliveryEmptyState icon={<FiFileText size={22} />} text="No documents uploaded yet" color={subTextColor} />
                  )}
                </DeliverySectionCard>

                {/* FrontApp Link */}
                <DeliveryLinkSection
                  title="FrontApp Link"
                  subtitle="(optional)"
                  placeholder="https://app.frontapp.com/..."
                  links={formData.frontAppLinks}
                  onAddLink={(link) => updateField('frontAppLinks', (prev) => [...prev, link])}
                  onDeleteLink={handleDeleteFrontAppLink}
                  isCompletedTab={isCompletedTab}
                  theme={themeTokens}
                />

                {/* Active Revisions to Deliver */}
                <DeliverySectionCard
                  title="ACTIVE REVISIONS TO DELIVER"
                  height="auto"
                  showSaveButton={false}
                  sectionBg={sectionBg}
                  borderColor={borderColor}
                  textColor={textColor}
                  subTextColor={subTextColor}
                  primaryColor={primaryColor}
                >
                  {activeRevisions.length > 0 ? (
                    <Flex sx={{ flexWrap: 'wrap', gap: '8px' }}>
                      {activeRevisions.map((rev) => (
                        <Text
                          key={rev}
                          sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: primaryColor,
                            backgroundColor: isDark ? 'rgba(119,67,219,0.18)' : 'rgba(119,67,219,0.08)',
                            border: `1px solid ${isDark ? 'rgba(119,67,219,0.5)' : 'rgba(119,67,219,0.25)'}`,
                            borderRadius: '6px',
                            padding: '3px 10px',
                          }}
                        >
                          {rev}
                        </Text>
                      ))}
                    </Flex>
                  ) : (
                    <DeliveryEmptyState icon={<FiFileText size={22} />} text="No revisions yet" color={subTextColor} />
                  )}
                </DeliverySectionCard>
              </Flex>
              <Flex sx={{ flexDirection: 'column', gap: '12px', minWidth: 0, minHeight: 0 }}>
                {/* Delivery Notes by Trade Table */}
                <Box sx={tableSlotSx(3, 200)}>
                  <DeliveryTable
                    type="deliveryNotes"
                    notes={formData.tradeNotes}
                    trades={availableTrades}
                    activeTrade={activeTrade}
                    onTradeChange={setActiveTrade}
                    onToggleInclude={handleToggleInclude}
                    onEditNote={handleEditNote}
                    cardBg={cardBg}
                    sectionBg={sectionBg}
                    borderColor={borderColor}
                    textColor={textColor}
                    subTextColor={subTextColor}
                    primaryColor={primaryColor}
                    height="auto"
                    isLoading={savingSection === 'Delivery Notes'}
                    isCompletedTab={isCompletedTab}
                  />
                </Box>

                {/* Sheet Index Table */}
                <Box sx={tableSlotSx(2, 160)}>
                  <DeliveryTable
                    type="sheetIndex"
                    sheetIndexes={formData.sheetIndexes}
                    onStartEdit={handleStartEditItem}
                    onDeleteSheet={handleDeleteSheetItem}
                    onAddClick={() => {
                      if (!showAddSheet) {
                        setSheetTextarea('');
                        setShowAddSheet(true);
                      } else {
                        setShowAddSheet(false);
                        setSheetTextarea('');
                      }
                      setSheetError(null);
                    }}
                    showAddSheet={showAddSheet}
                    sheetTextarea={sheetTextarea}
                    onSheetTextareaChange={(val) => {
                      setSheetTextarea(val);
                      if (sheetError) setSheetError(null);
                    }}
                    sheetError={sheetError}
                    onAddSubmit={handleAddSheetSubmit}
                    onCancelAdd={() => {
                      setShowAddSheet(false);
                      setSheetTextarea('');
                      setSheetError(null);
                      setEditingSheetId(null);
                    }}
                    editingSheetId={editingSheetId}
                    cardBg={cardBg}
                    sectionBg={sectionBg}
                    borderColor={borderColor}
                    textColor={textColor}
                    subTextColor={subTextColor}
                    primaryColor={primaryColor}
                    height="auto"
                    isCompletedTab={isCompletedTab}
                  />
                </Box>
              </Flex>
            </Box>
          </Box>
        </ModalBody>

        {!isCompletedTab && (
          <ModalFooter
            style={{
              backgroundColor: cardBg,
              borderTop: `1px solid ${borderColor}`,
              padding: '10px 20px',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <Button
              onClick={handleSaveAll}
              disabled={isSaveDisabled}
              variant="CancelButton"
              sx={{
                padding: '10px 24px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                opacity: isSaveDisabled ? 0.5 : 1,
                mr: '12px',
              }}
            >
              {isSavingAll ? (
                <Flex sx={{ alignItems: 'center', gap: 2 }}>
                  <Spinner size={16} color={primaryColor} /> Saving...
                </Flex>
              ) : (
                'Save'
              )}
            </Button>

            {!isManual && (
              <Box variant="cards.deliverTooltipWrapper">
                {isDeliverDisabled && !isDelivering && (
                  <Box
                    className="custom-deliver-tooltip"
                    variant="cards.deliverTooltip"
                  >
                    {!isEditAllowed
                      ? 'Only Project Managers and Super Admin have permission to edit'
                      : isAnySectionDirty
                        ? 'Please save changes before delivering'
                        : isAlreadyDelivered
                          ? 'Stage is already delivered'
                          : !hasDeliveryDate
                            ? 'Please select delivery date'
                            : isWaitingForPaymentRed && !isPublishingReady
                              ? 'Payment is pending and Publishing Ready is not checked'
                              : isWaitingForPaymentRed
                                ? 'Payment is pending'
                                : 'Publishing Ready checkbox is not ready'}
                  </Box>
                )}
                <Button
                  onClick={handleDeliver}
                  disabled={isDeliverDisabled}
                  variant="SaveButton"
                  sx={{
                    padding: '10px 24px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: isDeliverDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDeliverDisabled ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minWidth: '110px',
                    minHeight: '40px',
                  }}
                >
                  {isDelivering && !showConfirmDeliver ? (
                    <Flex sx={{ alignItems: 'center', gap: '8px' }}>
                      <Spinner size={16} color="#FFFFFF" />
                      <span>Delivering...</span>
                    </Flex>
                  ) : (
                    'Deliver'
                  )}
                </Button>
              </Box>
            )}
          </ModalFooter>
        )}
      </Modal>

      {/* Confirmation Modal for Deliver action */}
      <Modal isOpen={showConfirmDeliver} toggle={() => !isDelivering && setShowConfirmDeliver(false)} centered style={{ zIndex: 9999 }}>
        <ModalHeader toggle={() => !isDelivering && setShowConfirmDeliver(false)} style={{ backgroundColor: isDark ? '#1E293B' : '#FFFFFF', color: textColor, borderBottom: `1px solid ${borderColor}` }}>
          Confirm Delivery
        </ModalHeader>
        <ModalBody style={{ backgroundColor: isDark ? '#1E293B' : '#FFFFFF', color: textColor, padding: '24px' }}>
          <Text sx={{ fontSize: '14px', color: textColor }}>
            Are you sure you want to deliver this stage?
          </Text>
        </ModalBody>
        <ModalFooter style={{ backgroundColor: isDark ? '#1E293B' : '#FFFFFF', borderTop: `1px solid ${borderColor}`, padding: '16px 24px' }}>
          <Button
            type="button"
            variant="CancelButton"
            onClick={() => setShowConfirmDeliver(false)}
            disabled={isDelivering}
            sx={{
              padding: '6px 16px',
              fontSize: '13px',
              mr: '10px',
              cursor: isDelivering ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="SaveButton"
            onClick={proceedDeliver}
            disabled={isDelivering}
            sx={{
              padding: '6px 18px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: isDelivering ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              minWidth: '90px',
            }}
          >
            {isDelivering ? (
              <Flex sx={{ alignItems: 'center', gap: '6px' }}>
                <Spinner size={14} color="#FFFFFF" />
                <span>Delivering...</span>
              </Flex>
            ) : (
              'Confirm'
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default EditDeliveryDetailsModal;
